import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { getErrorMessage } from "../lib/utils";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Badge } from "../components/ui/badge";
import { ArrowUpFromLine, Package, Plus, History, Pencil, Trash2, Download, FileSpreadsheet, File } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const StockExitPage = () => {
  const { user, hasPermission } = useAuth();
  const isAdmin = user?.role === "admin";
  const [products, setProducts] = useState([]);
  const [exits, setExits] = useState([]);
  const [exitReasons, setExitReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState({
    product_id: "",
    quantity: "",
    reason: "",
    client_name: "",
    treatment_type: "",
    exit_date: new Date().toISOString().split("T")[0],
  });

  // Edit state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingExit, setEditingExit] = useState(null);
  const [editFormData, setEditFormData] = useState({
    product_id: "",
    quantity: "",
    reason: "",
    client_name: "",
    treatment_type: "",
    exit_date: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, exitsRes, reasonsRes] = await Promise.all([
        api.get("/products"),
        api.get("/stock-exits"),
        api.get("/settings/exit-reasons"),
      ]);
      setProducts(productsRes.data);
      setExits(exitsRes.data);
      setExitReasons(reasonsRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleProductChange = (productId) => {
    const product = products.find((p) => p.id === productId);
    setSelectedProduct(product);
    setFormData({ ...formData, product_id: productId });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!hasPermission("add_exit")) {
      toast.error("Você não tem permissão para registrar saídas");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/stock-exits", {
        ...formData,
        quantity: parseInt(formData.quantity),
        exit_date: formData.exit_date || null,
      });
      toast.success("Saída registrada com sucesso!");
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Erro ao registrar saída"));
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      product_id: "",
      quantity: "",
      reason: "",
      client_name: "",
      treatment_type: "",
      exit_date: new Date().toISOString().split("T")[0],
    });
    setSelectedProduct(null);
  };

  // Edit functions
  const openEditDialog = (exit) => {
    setEditingExit(exit);
    setEditFormData({
      product_id: exit.product_id,
      quantity: exit.quantity.toString(),
      reason: exit.reason,
      client_name: exit.client_name || "",
      treatment_type: exit.treatment_type || "",
      exit_date: exit.exit_date || exit.created_at?.split("T")[0] || "",
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingExit) return;

    try {
      await api.put(`/stock-exits/${editingExit.id}`, {
        product_id: editFormData.product_id,
        quantity: parseInt(editFormData.quantity),
        reason: editFormData.reason,
        client_name: editFormData.client_name || null,
        treatment_type: editFormData.treatment_type || null,
        exit_date: editFormData.exit_date || null,
      });
      toast.success("Saída atualizada com sucesso!");
      setEditDialogOpen(false);
      setEditingExit(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Erro ao atualizar saída"));
    }
  };

  const handleDelete = async (exit) => {
    try {
      await api.delete(`/stock-exits/${exit.id}`);
      toast.success("Saída excluída com sucesso! Estoque restaurado.");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir saída");
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    try {
      return format(new Date(dateStr), "dd/MM/yyyy HH:mm", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    if (exits.length === 0) {
      toast.error("Sem dados para exportar");
      return;
    }

    const worksheetData = exits.map(exit => ({
      "Produto": exit.product_name,
      "Quantidade": exit.quantity,
      "Motivo": exit.reason,
      "Observação": exit.notes || "",
      "Responsável": exit.user_name,
      "Data de Saída": formatDateTime(exit.created_at),
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Saídas");

    // Auto-adjust column widths
    worksheet["!cols"] = [
      { wch: 25 }, { wch: 12 }, { wch: 20 },
      { wch: 25 }, { wch: 20 }, { wch: 18 }
    ];

    XLSX.writeFile(workbook, `saidas_estoque_${format(new Date(), "yyyy-MM-dd")}.xlsx`);
    toast.success("Relatório Excel exportado com sucesso!");
  };

  // Export to PDF
  const exportToPDF = () => {
    if (exits.length === 0) {
      toast.error("Sem dados para exportar");
      return;
    }

    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(18);
    doc.setTextColor(31, 41, 55);
    doc.text("Histórico de Saídas de Estoque", 14, 22);
    
    // Date
    doc.setFontSize(10);
    doc.setTextColor(107, 114, 128);
    doc.text(`Gerado em: ${format(new Date(), "dd/MM/yyyy HH:mm")}`, 14, 30);

    // Table
    const tableData = exits.map(exit => [
      exit.product_name,
      exit.quantity.toString(),
      exit.reason,
      exit.user_name,
      formatDateTime(exit.created_at),
    ]);

    autoTable(doc, {
      head: [["Produto", "Qtd", "Motivo", "Responsável", "Data"]],
      body: tableData,
      startY: 38,
      styles: { 
        fontSize: 8,
        cellPadding: 2,
      },
      headStyles: { 
        fillColor: [239, 68, 68],
        textColor: 255,
        fontStyle: "bold"
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: 14, right: 14 },
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(156, 163, 175);
      doc.text(
        `Página ${i} de ${pageCount} - EstoqueClinic`,
        doc.internal.pageSize.getWidth() / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: "center" }
      );
    }

    doc.save(`saidas_estoque_${format(new Date(), "yyyy-MM-dd")}.pdf`);
    toast.success("Relatório PDF exportado com sucesso!");
  };

  const isExpiringSoon = (dateStr) => {
    try {
      const expDate = new Date(dateStr);
      const today = new Date();
      const diffDays = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));
      return diffDays <= 30 && diffDays >= 0;
    } catch {
      return false;
    }
  };

  const isExpired = (dateStr) => {
    try {
      return new Date(dateStr) < new Date();
    } catch {
      return false;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="stock-exit-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
          Saída de Estoque
        </h1>
        <p className="text-slate-500 mt-1">
          Registre o consumo ou venda de produtos
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Exit Form */}
        <div className="lg:col-span-1">
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <ArrowUpFromLine className="h-5 w-5 text-red-600" />
                Nova Saída
              </CardTitle>
              <CardDescription>
                Registre a saída de produtos do estoque
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="product">Produto *</Label>
                  <Select
                    value={formData.product_id}
                    onValueChange={handleProductChange}
                  >
                    <SelectTrigger data-testid="exit-product-select">
                      <SelectValue placeholder="Selecione o produto" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.filter((p) => p.current_stock > 0 && p.id && p.name).map((product) => (
                        <SelectItem key={product.id} value={product.id}>
                          <div className="flex items-center gap-2">
                            <Package className="h-4 w-4" />
                            {product.name} ({product.current_stock} {product.unit})
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="quantity">Quantidade *</Label>
                  <Input
                    id="quantity"
                    type="number"
                    min="1"
                    max={selectedProduct?.current_stock || 999999}
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    required
                    data-testid="exit-quantity-input"
                  />
                  {selectedProduct && (
                    <p className="text-xs text-slate-500">
                      Disponível: {selectedProduct.current_stock} {selectedProduct.unit}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reason">Motivo da Saída *</Label>
                  <Select
                    value={formData.reason}
                    onValueChange={(value) => setFormData({ ...formData, reason: value })}
                  >
                    <SelectTrigger data-testid="exit-reason-select">
                      <SelectValue placeholder="Selecione o motivo" />
                    </SelectTrigger>
                    <SelectContent>
                      {exitReasons.filter(r => r.name && r.name.trim()).map((reason) => (
                        <SelectItem key={reason.id} value={reason.name}>
                          {reason.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="client_name">Cliente (opcional)</Label>
                  <Input
                    id="client_name"
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    placeholder="Nome do cliente"
                    data-testid="exit-client-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="treatment_type">Tipo de Tratamento (opcional)</Label>
                  <Input
                    id="treatment_type"
                    value={formData.treatment_type}
                    onChange={(e) => setFormData({ ...formData, treatment_type: e.target.value })}
                    placeholder="Ex: Preenchimento labial"
                    data-testid="exit-treatment-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="exit_date">Data da Saida</Label>
                  <Input
                    id="exit_date"
                    type="date"
                    value={formData.exit_date}
                    onChange={(e) => setFormData({ ...formData, exit_date: e.target.value })}
                    data-testid="exit-date-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={submitting || !hasPermission("add_exit") || !selectedProduct}
                  data-testid="exit-submit-btn"
                >
                  {submitting ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                  ) : (
                    <>
                      <Plus className="mr-2 h-4 w-4" />
                      Registrar Saída
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Recent Exits */}
        <div className="lg:col-span-2">
          <Card className="card-dashboard">
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <History className="h-5 w-5 text-slate-500" />
                    Histórico de Saídas
                  </CardTitle>
                  <CardDescription>
                    Últimas 100 saídas registradas no sistema
                    {isAdmin && " - Clique para editar ou excluir"}
                  </CardDescription>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={exits.length === 0}
                      className="gap-2"
                      data-testid="export-exits-btn"
                    >
                      <Download className="h-4 w-4" />
                      Exportar
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={exportToExcel}>
                      <FileSpreadsheet className="h-4 w-4 mr-2 text-green-600" />
                      Exportar Excel
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={exportToPDF}>
                      <File className="h-4 w-4 mr-2 text-red-600" />
                      Exportar PDF
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {exits.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                  <ArrowUpFromLine className="h-12 w-12 mb-4 text-slate-300" />
                  <p>Nenhuma saída registrada</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Produto</TableHead>
                        <TableHead>Motivo</TableHead>
                        <TableHead className="text-right">Qtd</TableHead>
                        <TableHead>Registrado por</TableHead>
                        <TableHead>Data</TableHead>
                        {isAdmin && <TableHead className="text-right">Ações</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {exits.slice(0, 100).map((exit) => (
                        <TableRow key={exit.id} className="table-row-zebra">
                          <TableCell className="font-medium">
                            {exit.product_name}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{exit.reason}</Badge>
                          </TableCell>
                          <TableCell className="text-right font-semibold text-red-600">
                            -{exit.quantity}
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {exit.created_by}
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {formatDateTime(exit.created_at)}
                          </TableCell>
                          {isAdmin && (
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openEditDialog(exit)}
                                  title="Editar"
                                  data-testid={`edit-exit-${exit.id}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="text-red-500 hover:text-red-700"
                                      title="Excluir"
                                      data-testid={`delete-exit-${exit.id}`}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Excluir saída?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Esta ação irá remover a saída e restaurar as quantidades no estoque.
                                        O registro será movido para a lixeira por 24 horas.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => handleDelete(exit)}
                                        className="bg-red-600 hover:bg-red-700"
                                      >
                                        Excluir
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Editar Saída de Estoque</DialogTitle>
            <DialogDescription>
              Altere os dados da saída. As quantidades serão ajustadas automaticamente.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-product">Produto</Label>
                <Select
                  value={editFormData.product_id}
                  onValueChange={(value) => setEditFormData({ ...editFormData, product_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o produto" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.filter(p => p.id && p.name).map((product) => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-quantity">Quantidade</Label>
                <Input
                  id="edit-quantity"
                  type="number"
                  min="1"
                  value={editFormData.quantity}
                  onChange={(e) => setEditFormData({ ...editFormData, quantity: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-reason">Motivo</Label>
                <Select
                  value={editFormData.reason}
                  onValueChange={(value) => setEditFormData({ ...editFormData, reason: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o motivo" />
                  </SelectTrigger>
                  <SelectContent>
                    {exitReasons.filter(r => r.name && r.name.trim()).map((reason) => (
                      <SelectItem key={reason.id} value={reason.name}>
                        {reason.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-client">Cliente</Label>
                <Input
                  id="edit-client"
                  value={editFormData.client_name}
                  onChange={(e) => setEditFormData({ ...editFormData, client_name: e.target.value })}
                  placeholder="Nome do cliente"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-treatment">Tipo de Tratamento</Label>
                <Input
                  id="edit-treatment"
                  value={editFormData.treatment_type}
                  onChange={(e) => setEditFormData({ ...editFormData, treatment_type: e.target.value })}
                  placeholder="Ex: Preenchimento labial"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-exit-date">Data da Saida</Label>
                <Input
                  id="edit-exit-date"
                  type="date"
                  value={editFormData.exit_date}
                  onChange={(e) => setEditFormData({ ...editFormData, exit_date: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">Salvar Alterações</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StockExitPage;
